import React, { Fragment, useContext, useEffect, useState } from "react"

import styles from "./styles";
import DashPage from "../dash/DashPage";
import Splitter from "../../components/splitter/Splitter";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import { FiChevronLeft, FiChevronRight, FiCopy, FiSave, FiScissors, FiTrash } from "react-icons/fi";
import ToolBox from "./components/toolbox/ToolBox";

import FlowRender from "./components/flow/FlowRender";
import ToolBoxItems from "./components/toolbox/ToolBoxItems";
import PropertyEditor from "./components/propEditor/PropertyEditor";
import flex from "../../styles/flex";
import { FaPaste } from "react-icons/fa";
import { Workflow, IWorkflowToolBoxItem, WorkflowAction, api, RequestStatus } from "../../logic/api";
import { useParams } from "react-router-dom";
import { SessionContext } from "../../utility/useSession";
import { ProgressView } from "../../components/notifications/progress/ProgressView";
import ErrorDialog from "../../components/notifications/errorDialog/ErrorDialog";
import { v4 } from "uuid";
import { useTranslation } from "react-i18next";

interface IWorkflowParams {
    productCode: string;
}
const WorkflowDesigner = () => {

    const params: IWorkflowParams = useParams();
    const session = useContext(SessionContext);
    const getWorkflow = api.hooks.workflow.getWorkflow();
    const saveWorkflow = api.hooks.workflow.saveWorkflow();

    const toolBoxItems = ToolBoxItems();
    const { t } = useTranslation();

    const [flow, setFlow] = useState<Workflow | null>(null);
    const [copiedAction, setCopiedAction] = useState<WorkflowAction | null>(null);
    const [cut, setCut] = useState<boolean>(false);
    const [showSavedDialog, setShowSavedDialog] = useState(false);

    useEffect(() => {
        let hasCancelled = false;
        getWorkflow.send(session.current.context, {
            productCode: params.productCode
        }, (result) => {
            if (hasCancelled == false) {
                if (result != null) {
                    setFlow(result);
                }
            }
        });
        return () => {
            hasCancelled = true;
        }
    }, [params.productCode]);


    const [selectedId, setSelectedId] = useState<string | null>(null);


    const saveFlow = () => {
        if (flow == null) {
            return;
        }
        saveWorkflow.send(session.current.context, {
            productCode: params.productCode,
            workflow: flow
        }, (result) => {
            if (result != null) {
                setFlow(result);
                setShowSavedDialog(true);
            }
        });
    }

    const getFlow = () => {
        if (flow == null) {
            return null;
        }
        const b: Workflow = JSON.parse(JSON.stringify(flow));
        return b;
    }

    const getActionById = (id: string | null) => {
        if (flow == null) {
            return null;
        }
        if (id == null) {
            return null;
        }
        const actions = flow.actions.filter((a) => {
            return a.id == id;
        });
        if (actions.length == 0) {
            return null;
        }
        return actions[0];
    };

    const selectedItem = getActionById(selectedId);

    const generateNewId = () => {
        return v4().toString();
    }

    const insertAction = (item: IWorkflowToolBoxItem) => {
        if (flow == null) {
            return;
        }
        item = JSON.parse(JSON.stringify(item));

        const newAction: WorkflowAction = {
            id: generateNewId(),
            parent: selectedId,
            toolRef: item.ref,
            data: {},
            workflow: null,
            serial: null,
            orderIndex: flow.actions.length
        };

        for (const prop of item.properties) {
            newAction.data[prop.name] = prop.defaultValue;
        }

        const flowCopy = getFlow();
        if (flowCopy == null) {
            return;
        }
        flowCopy.actions.push(newAction);

        setFlow(flowCopy);
        setSelectedId(newAction.id);
    }


    const deleteAction = () => {
        if (flow == null) {
            return;
        }
        if (selectedId == null) {
            return;
        }
        const targetAction = getActionById(selectedId);
        if (targetAction == null) {
            return;
        }
        const index = flow.actions.indexOf(targetAction);

        const flowCopy = getFlow();
        if (flowCopy == null) {
            return;
        }
        flowCopy.actions.splice(index, 1);

        setFlow(flowCopy);

        setSelectedId(null);
    }



    const copyAction = (isCut: boolean) => {
        if (flow == null) {
            return;
        }
        if (selectedId == null) {
            return;
        }
        const targetAction = getActionById(selectedId);
        if (targetAction == null) {
            return;
        }
        if (isCut == true) {
            setSelectedId(null);
            deleteAction();
        }
        setCut(isCut);
        setCopiedAction(JSON.parse(JSON.stringify(targetAction)));
    }

    const pasteAction = () => {
        if (copiedAction == null) {
            return;
        }
        const newAction = JSON.parse(JSON.stringify(copiedAction));
        if (cut == false) {
            newAction.id = generateNewId();
        }
        const flowCopy = getFlow();
        if (flowCopy == null) {
            return;
        }
        flowCopy.actions.push(newAction);
        if (cut == true) {
            setCopiedAction(null);
        }
        setCut(false);
        setFlow(flowCopy);
        setSelectedId(newAction.id);
    }

    const moveLeft = () => {
        if (flow == null) {
            return;
        }
        if (selectedId == null) {
            return;
        }
        const targetAction = getActionById(selectedId);
        if (targetAction == null) {
            return;
        }
        const index = flow.actions.indexOf(targetAction);

        const flowCopy = getFlow();
        if (flowCopy == null) {
            return;
        }

        const destIndex = index - 1;

        if (destIndex < 0) {
            return;
        }

        const replacementAction = flowCopy.actions[destIndex];
        flowCopy.actions[destIndex] = targetAction;
        flowCopy.actions[index] = replacementAction;

        setFlow(flowCopy);

    }

    const moveRight = () => {
        if (flow == null) {
            return;
        }
        if (selectedId == null) {
            return;
        }
        const targetAction = getActionById(selectedId);
        if (targetAction == null) {
            return;
        }
        const index = flow.actions.indexOf(targetAction);

        const flowCopy = getFlow();
        if (flowCopy == null) {
            return;
        }

        const destIndex = index + 1;

        if (destIndex < 0) {
            return;
        }

        const replacementAction = flowCopy.actions[destIndex];
        if (replacementAction == null) {
            return;
        }
        flowCopy.actions[destIndex] = targetAction;
        flowCopy.actions[index] = replacementAction;

        setFlow(flowCopy);
    }

    const updateAction = (action: WorkflowAction) => {
        if (flow == null) {
            return;
        }
        const targetAction = getActionById(action.id);
        if (targetAction == null) {
            return;
        }
        const index = flow.actions.indexOf(targetAction);

        const flowCopy = getFlow();
        if (flowCopy == null) {
            return;
        }
        flowCopy.actions[index] = action;

        setFlow(flowCopy);

        setSelectedId(action.id);
    }


    return (
        <DashPage noScroll={true}>

            <ErrorDialog err={getWorkflow.err}></ErrorDialog>
            <ErrorDialog err={saveWorkflow.err}></ErrorDialog>


            <Dialog open={saveWorkflow.status == RequestStatus.loading || showSavedDialog == true}>
                {

                    showSavedDialog == true && (
                        <Fragment>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-slide-description">{t("workflow.workflowSaved")}</DialogContentText>
                            </DialogContent>
                            <DialogActions>

                                <Button onClick={() => setShowSavedDialog(false)} color="primary">{t("general.ok")}</Button>
                            </DialogActions>
                        </Fragment>
                    )
                }
                {saveWorkflow.status == RequestStatus.loading && (
                    <Fragment>
                        <DialogTitle id="alert-dialog-slide-title">{t("workflow.savingWorkflow")}</DialogTitle>
                        <DialogContent>

                            <div style={{ textAlign: "center", padding: 20 }}>
                                <CircularProgress></CircularProgress>
                            </div>
                        </DialogContent>
                    </Fragment>
                )}

            </Dialog>

            <ProgressView status={getWorkflow.status}>
                {
                    flow != null && (
                        <div style={styles.wrapper}>

                            <div style={styles.leftPanel}>
                                <Splitter>
                                    <div style={styles.toolBar}>

                                    </div>
                                    <div>
                                        <ToolBox selected={selectedItem} toolBoxItems={toolBoxItems} onItemClicked={(item) => {
                                            insertAction(item);
                                        }}></ToolBox>
                                    </div>
                                </Splitter>

                            </div>
                            <div style={styles.centerPanel}>
                                <Splitter>
                                    <div style={{ ...styles.toolBar, ...flex.flexRows }}>
                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                disabled={selectedId == null}
                                                onClick={() => {
                                                    deleteAction();
                                                }}>
                                                <FiTrash></FiTrash>
                                            </IconButton>
                                        </div>
                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                disabled={selectedId == null}
                                                onClick={() => {
                                                    copyAction(true);
                                                }}>
                                                <FiScissors></FiScissors>
                                            </IconButton>
                                        </div>
                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                disabled={selectedId == null}
                                                onClick={() => {
                                                    copyAction(false);
                                                }}>
                                                <FiCopy></FiCopy>
                                            </IconButton>
                                        </div>
                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                disabled={copiedAction == null}
                                                onClick={() => {
                                                    pasteAction();
                                                }}>
                                                <FaPaste></FaPaste>
                                            </IconButton>
                                        </div>
                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                disabled={selectedId == null}
                                                onClick={() => {
                                                    moveLeft();
                                                }}>
                                                <FiChevronLeft></FiChevronLeft>
                                            </IconButton>
                                        </div>
                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                disabled={selectedId == null}
                                                onClick={() => {
                                                    moveRight();
                                                }}>
                                                <FiChevronRight></FiChevronRight>
                                            </IconButton>
                                        </div>
                                        <div style={flex.flexFill}>

                                        </div>

                                        <div style={flex.flexAuto}>
                                            <IconButton
                                                onClick={() => {
                                                    saveFlow();
                                                }}>
                                                <FiSave></FiSave>
                                            </IconButton>
                                        </div>

                                    </div>
                                    <div>
                                        <FlowRender isProcessView={false} selectedId={selectedId} flow={flow} toolBoxItems={toolBoxItems} onActionSelected={(action) => {
                                            if (action) {
                                                setSelectedId(action.id);
                                            } else {
                                                setSelectedId(null);
                                            }
                                        }}></FlowRender>
                                    </div>
                                </Splitter>
                            </div>
                            <div style={styles.rightPanel}>
                                <Splitter>
                                    <div style={styles.toolBar}>

                                    </div>
                                    <div>
                                        <PropertyEditor selectedId={selectedId} flow={flow} toolBoxItems={toolBoxItems} onUpdate={(action) => {
                                            updateAction(action);
                                        }}></PropertyEditor>
                                    </div>
                                </Splitter>
                            </div>
                        </div>
                    )
                }
            </ProgressView>



        </DashPage>
    );
}

export default WorkflowDesigner;
